// Settings store (IMPLEMENTATION §3 M7 "Settings", UX §2 Settings / §7): the player's preferences, saved in
// the save envelope's `meta.settings` and applied to the audio (M9 facade), the render quality and the
// comfort flags other layers read:
//   reduceMotion  System / On / Off; the EFFECTIVE flag (System follows prefers-reduced-motion) goes to
//                 fx/motion.ts setReduceMotion — M10's FX layer reads it there (lead decision L16); UX §5 RM
//   lowQuality    DPR 1.5 + 30 fps (main.ts / the renderer) + fewer particles (M10 reads it)
//   hintsOn       tutorial tips and the hint finger (the cold-open lines always show)
// `settingsFrom` takes only well-formed fields of a loaded record (a hand-edited or older save keeps the
// defaults for the rest); `settingsRecord` is what goes back into meta.settings.
import { computed, type ReadonlySignal, signal } from '@preact/signals';
import { type AudioSettings, audioSettingsFrom, DEFAULT_AUDIO_SETTINGS } from '../audio/index.ts';

export type ReduceMotionMode = 'system' | 'on' | 'off';

export interface Settings extends AudioSettings {
  reduceMotion: ReduceMotionMode;
  lowQuality: boolean;
  hints: boolean;
}

export const DEFAULT_SETTINGS: Readonly<Settings> = {
  ...DEFAULT_AUDIO_SETTINGS,
  reduceMotion: 'system',
  lowQuality: false,
  hints: true,
};

/** The well-formed settings of a saved record (`meta.settings`), defaults for everything else. */
export function settingsFrom(x: unknown): Settings {
  const out: Settings = { ...DEFAULT_SETTINGS, ...audioSettingsFrom(x) };
  if (typeof x !== 'object' || x === null) return out;
  const r = x as Record<string, unknown>;
  if (r.reduceMotion === 'system' || r.reduceMotion === 'on' || r.reduceMotion === 'off')
    out.reduceMotion = r.reduceMotion;
  if (typeof r.lowQuality === 'boolean') out.lowQuality = r.lowQuality;
  if (typeof r.hints === 'boolean') out.hints = r.hints;
  return out;
}

/** The record saved as `meta.settings` (plain JSON; volumes rounded to 1/1000). */
export function settingsRecord(s: Settings): Record<string, unknown> {
  const vol = (v: number): number => Math.round(Math.min(1, Math.max(0, v)) * 1000) / 1000;
  return {
    master: vol(s.master),
    sfx: vol(s.sfx),
    muted: s.muted,
    silentSwitch: s.silentSwitch,
    reduceMotion: s.reduceMotion,
    lowQuality: s.lowQuality,
    hints: s.hints,
  };
}

/** Reduce motion in effect: System follows the OS preference. */
export function effectiveReduceMotion(mode: ReduceMotionMode, system: boolean): boolean {
  return mode === 'system' ? system : mode === 'on';
}

export const settingsSig = signal<Settings>({ ...DEFAULT_SETTINGS });
/** `prefers-reduced-motion: reduce` (main.ts keeps it in sync with the media query). */
export const systemReducedMotion = signal(false);

/** The effective reduce-motion flag; main.ts mirrors it into fx/motion.ts (L16: M10's FX reads it there). */
export const reduceMotionSig: ReadonlySignal<boolean> = computed(() =>
  effectiveReduceMotion(settingsSig.value.reduceMotion, systemReducedMotion.value),
);
/** Low quality: DPR 1.5, 30 fps, fewer particles (main.ts applyQuality mirrors it into fx/quality.ts, L17). */
export const lowQuality: ReadonlySignal<boolean> = computed(() => settingsSig.value.lowQuality);
export const hintsOn: ReadonlySignal<boolean> = computed(() => settingsSig.value.hints);

/** Changes some settings (the Settings sheet, the HUD mute button). */
export function updateSettings(patch: Partial<Settings>): void {
  settingsSig.value = { ...settingsSig.value, ...patch };
}

/** Where settings take effect (main.ts: the M9 audio facade, the renderer quality, the save meta). */
export interface SettingsSinks {
  audio: {
    setVolumes(v: { master?: number; sfx?: number }): void;
    setMuted(m: boolean): void;
    setSilentSwitchMode(m: AudioSettings['silentSwitch']): void;
  };
  quality(low: boolean): void;
  persist(record: Record<string, unknown>): void;
}

/** Applies what changed between `prev` (null = everything) and `next`, then persists `next`. */
export function applySettings(prev: Settings | null, next: Settings, sinks: SettingsSinks): void {
  if (prev === null || prev.master !== next.master || prev.sfx !== next.sfx)
    sinks.audio.setVolumes({ master: next.master, sfx: next.sfx });
  if (prev === null || prev.muted !== next.muted) sinks.audio.setMuted(next.muted);
  if (prev === null || prev.silentSwitch !== next.silentSwitch)
    sinks.audio.setSilentSwitchMode(next.silentSwitch);
  if (prev === null || prev.lowQuality !== next.lowQuality) sinks.quality(next.lowQuality);
  sinks.persist(settingsRecord(next));
}
